import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BrainCircuit, LogOut, User, ChevronRight, HelpCircle } from 'lucide-react';

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();

  // Get current page info for breadcrumbs
  const getPageInfo = () => {
    switch (location.pathname) {
      case '/carpe-diem':
        return { title: 'Carpe Diem - Seize the Day', subtitle: 'Mindfulness at Work - What are you working on right now?' };
      case '/dashboard':
        return { title: 'Analytics Dashboard', subtitle: 'Business Analytics' };
      case '/profitability-performance':
        return { title: 'Profitability & Performance', subtitle: 'Margins, utilisation & outcomes' };
      case '/':
      default:
        return { title: 'Brevity AI', subtitle: 'Central Operations Hub' };
    }
  };

  const pageInfo = getPageInfo();
  const isSubPage = location.pathname !== '/';

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand with Breadcrumbs */}
          <div className="flex items-center">
            <BrainCircuit className="h-8 w-8 text-blue-600" />
            <div className="ml-3 flex items-center">
              {isSubPage ? (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate('/')}
                    className="text-xl font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Brevity AI
                  </button>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="flex items-center">
                      <span className="text-xl font-semibold text-gray-900">{pageInfo.title}</span>
                      {location.pathname === '/carpe-diem' && (
                        <a
                          href="https://medium.com/@RationalBadger/carpe-diem-seize-the-day-a51906828cb9"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Read the 'Carpe Diem - Seize the Day' article"
                          title="Read the article"
                          className="ml-2 text-gray-400 hover:text-blue-600"
                        >
                          <HelpCircle className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-tight">{pageInfo.subtitle}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <span className="text-xl font-semibold text-gray-900">{pageInfo.title}</span>
                  <p className="text-xs text-gray-500 leading-tight">{pageInfo.subtitle}</p>
                </div>
              )}
            </div>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {currentUser && (
              <>
                <div className="flex items-center space-x-2">
                  {currentUser.photoURL ? (
                    <img
                      src={currentUser.photoURL}
                      alt={currentUser.displayName}
                      className="h-8 w-8 rounded-full object-cover border border-gray-200"
                      onError={(e) => {
                        // Hide the broken image and show fallback
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center"
                    style={{ display: currentUser.photoURL ? 'none' : 'flex' }}
                  >
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {currentUser.displayName}
                  </span>
                </div>
                
                <button
                  onClick={handleLogout}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}; 